import { useState, useEffect, useCallback } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import type {
  EmploymentType,
  WageType,
  EmploymentHistoryRecord,
  CreateHistoryRequest,
} from '../../types';
import {
  EMPLOYMENT_TYPES,
  EMPLOYMENT_TYPE_LABELS,
  WAGE_TYPES,
  WAGE_TYPE_LABELS,
} from '../../types';
import { FEDERAL_STATE_LABELS } from '../../utils/historyLabels';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';

const DEPARTMENTS = ['SEO', 'SEA', 'Social', 'Content'] as const;

const DEPARTMENT_NAMES: Record<string, string> = {
  SEO: 'SEO',
  SEA: 'SEA',
  Social: 'Social Media',
  Content: 'Content',
};

const NONE_SENTINEL = '_none';

interface EmploymentChangeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateHistoryRequest) => Promise<void>;
  currentValues: EmploymentHistoryRecord | null;
  editRecord?: EmploymentHistoryRecord;
  mode: 'create' | 'edit';
}

function getTodayString(): string {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function isDateInPast(dateStr: string): boolean {
  if (!dateStr) return false;
  const today = getTodayString();
  return dateStr < today;
}

export function EmploymentChangeDialog({
  isOpen,
  onClose,
  onSave,
  currentValues,
  editRecord,
  mode,
}: EmploymentChangeDialogProps) {
  // Form state
  const [validFrom, setValidFrom] = useState('');
  const [employmentType, setEmploymentType] = useState<string>(NONE_SENTINEL);
  const [department, setDepartment] = useState<string>(NONE_SENTINEL);
  const [wageType, setWageType] = useState<string>(NONE_SENTINEL);
  const [hourlyWage, setHourlyWage] = useState('');
  const [monthlySalary, setMonthlySalary] = useState('');
  const [contractualHours, setContractualHours] = useState('');
  const [rv, setRv] = useState(false);
  const [hansefit, setHansefit] = useState(false);
  // Settings-Felder
  const [federalState, setFederalState] = useState<string>('NI');
  const [vacationDaysFullTime, setVacationDaysFullTime] = useState('30');
  const [workDaysPerWeekFullTime, setWorkDaysPerWeekFullTime] = useState('5');
  const [hoursPerDayFullTime, setHoursPerDayFullTime] = useState('8');
  const [changeNote, setChangeNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Reference values for diff calculation
  const [initialValues, setInitialValues] = useState<Record<string, unknown>>({});

  // Initialize form from props when dialog opens
  useEffect(() => {
    if (!isOpen) return;

    const source = mode === 'edit' ? editRecord : currentValues;

    const newValidFrom = mode === 'edit' && editRecord
      ? editRecord.validFrom
      : getTodayString();
    const newEmploymentType = source?.employmentType || NONE_SENTINEL;
    const newDepartment = source?.department || NONE_SENTINEL;
    const newWageType = source?.wageType || NONE_SENTINEL;
    const newHourlyWage = source?.hourlyWage != null ? String(source.hourlyWage) : '';
    const newMonthlySalary = source?.monthlySalary != null ? String(source.monthlySalary) : '';
    const newContractualHours = source?.contractualHours != null ? String(source.contractualHours) : '';
    const newRv = source?.rv ?? false;
    const newHansefit = source?.hansefit ?? false;
    // Settings-Felder
    const newFederalState = source?.federalState ?? 'NI';
    const newVacationDays = source?.vacationDaysFullTime != null ? String(source.vacationDaysFullTime) : '30';
    const newWorkDays = source?.workDaysPerWeekFullTime != null ? String(source.workDaysPerWeekFullTime) : '5';
    const newHoursPerDay = source?.hoursPerDayFullTime != null ? String(source.hoursPerDayFullTime) : '8';

    setValidFrom(newValidFrom);
    setEmploymentType(newEmploymentType);
    setDepartment(newDepartment);
    setWageType(newWageType);
    setHourlyWage(newHourlyWage);
    setMonthlySalary(newMonthlySalary);
    setContractualHours(newContractualHours);
    setRv(newRv);
    setHansefit(newHansefit);
    setFederalState(newFederalState);
    setVacationDaysFullTime(newVacationDays);
    setWorkDaysPerWeekFullTime(newWorkDays);
    setHoursPerDayFullTime(newHoursPerDay);
    setChangeNote('');
    setIsSaving(false);
    // Alten Save-Fehler aus einer frueheren Dialog-Session zuruecksetzen,
    // sonst zeigt das Reopen weiterhin die letzte Fehlermeldung.
    setSaveError(null);

    // Store initial values for diff
    setInitialValues({
      validFrom: newValidFrom,
      employmentType: newEmploymentType,
      department: newDepartment,
      wageType: newWageType,
      hourlyWage: newHourlyWage,
      monthlySalary: newMonthlySalary,
      contractualHours: newContractualHours,
      rv: newRv,
      hansefit: newHansefit,
      federalState: newFederalState,
      vacationDaysFullTime: newVacationDays,
      workDaysPerWeekFullTime: newWorkDays,
      hoursPerDayFullTime: newHoursPerDay,
    });
  }, [isOpen, mode, editRecord, currentValues]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setSaveError(null);

    try {
      const data: CreateHistoryRequest = {
        validFrom,
      };

      // Only send changed fields (diff against initial values)
      const currentEmploymentType = employmentType === NONE_SENTINEL ? null : employmentType as EmploymentType;
      const currentDepartment = department === NONE_SENTINEL ? null : department;
      const currentWageType = wageType === NONE_SENTINEL ? null : wageType as WageType;
      const currentHourlyWage = hourlyWage ? Number(hourlyWage) : null;
      const currentMonthlySalary = monthlySalary ? Number(monthlySalary) : null;
      const currentContractualHours = contractualHours ? Number(contractualHours) : null;

      const initialEmploymentType = initialValues.employmentType === NONE_SENTINEL ? null : initialValues.employmentType;
      const initialDepartment = initialValues.department === NONE_SENTINEL ? null : initialValues.department;
      const initialWageType = initialValues.wageType === NONE_SENTINEL ? null : initialValues.wageType;
      const initialHourlyWage = (initialValues.hourlyWage as string) ? Number(initialValues.hourlyWage) : null;
      const initialMonthlySalary = (initialValues.monthlySalary as string) ? Number(initialValues.monthlySalary) : null;
      const initialContractualHours = (initialValues.contractualHours as string) ? Number(initialValues.contractualHours) : null;

      if (currentEmploymentType !== initialEmploymentType) {
        data.employmentType = currentEmploymentType;
      }
      if (currentDepartment !== initialDepartment) {
        data.department = currentDepartment;
      }
      if (currentWageType !== initialWageType) {
        data.wageType = currentWageType;
      }
      if (currentHourlyWage !== initialHourlyWage) {
        data.hourlyWage = currentHourlyWage;
      }
      if (currentMonthlySalary !== initialMonthlySalary) {
        data.monthlySalary = currentMonthlySalary;
      }
      if (currentContractualHours !== initialContractualHours) {
        data.contractualHours = currentContractualHours;
      }
      if (rv !== initialValues.rv) {
        data.rv = rv;
      }
      if (hansefit !== initialValues.hansefit) {
        data.hansefit = hansefit;
      }
      // Settings-Felder
      if (federalState !== initialValues.federalState) {
        data.federalState = federalState;
      }
      if (vacationDaysFullTime !== initialValues.vacationDaysFullTime) {
        data.vacationDaysFullTime = Number(vacationDaysFullTime);
      }
      if (workDaysPerWeekFullTime !== initialValues.workDaysPerWeekFullTime) {
        data.workDaysPerWeekFullTime = Number(workDaysPerWeekFullTime);
      }
      if (hoursPerDayFullTime !== initialValues.hoursPerDayFullTime) {
        data.hoursPerDayFullTime = Number(hoursPerDayFullTime);
      }
      if (changeNote.trim()) {
        data.changeNote = changeNote.trim();
      }

      await onSave(data);
      onClose();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  }, [
    validFrom, employmentType, department, wageType,
    hourlyWage, monthlySalary, contractualHours,
    rv, hansefit,
    federalState, vacationDaysFullTime, workDaysPerWeekFullTime, hoursPerDayFullTime,
    changeNote, initialValues, onSave, onClose,
  ]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="p-4 pb-3">
          <DialogTitle>
            {mode === 'create' ? 'Vertragsänderung erfassen' : 'Eintrag bearbeiten'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Neue Vertragsdaten ab einem bestimmten Datum erfassen.'
              : 'Bestehenden Eintrag korrigieren.'}
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-4">
          {/* 1. Gültig-ab-Feld */}
          <div className="space-y-2">
            <Label htmlFor="ec-validFrom">Gültig ab *</Label>
            <Input
              type="date"
              id="ec-validFrom"
              value={validFrom}
              onChange={(e) => setValidFrom(e.target.value)}
              disabled={mode === 'edit'}
              required
              className={mode === 'edit' ? 'opacity-60' : ''}
            />
            {isDateInPast(validFrom) && mode === 'create' && (
              <div className="flex items-start gap-2 p-2 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700">
                <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  Achtung: Rückwirkende Änderung — kann vergangene Abrechnungen beeinflussen
                </p>
              </div>
            )}
          </div>

          {/* 2. Section: Beschäftigung */}
          <Separator />
          <div className="space-y-3">
            <h3 className="text-sm font-medium">Beschäftigung</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="ec-employmentType">Beschäftigungsart</Label>
                <Select value={employmentType} onValueChange={setEmploymentType}>
                  <SelectTrigger id="ec-employmentType">
                    <SelectValue placeholder="Keine Angabe" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_SENTINEL}>Keine Angabe</SelectItem>
                    {EMPLOYMENT_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {EMPLOYMENT_TYPE_LABELS[type]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ec-department">Abteilung</Label>
                <Select value={department} onValueChange={setDepartment}>
                  <SelectTrigger id="ec-department">
                    <SelectValue placeholder="Keine Angabe" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_SENTINEL}>Keine Angabe</SelectItem>
                    {DEPARTMENTS.map((dept) => (
                      <SelectItem key={dept} value={dept}>
                        {DEPARTMENT_NAMES[dept]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* 3. Section: Vergütung */}
          <Separator />
          <div className="space-y-3">
            <h3 className="text-sm font-medium">Vergütung</h3>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="ec-wageType">Lohnart</Label>
                <Select value={wageType} onValueChange={setWageType}>
                  <SelectTrigger id="ec-wageType">
                    <SelectValue placeholder="Keine Angabe" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_SENTINEL}>Keine Angabe</SelectItem>
                    {WAGE_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {WAGE_TYPE_LABELS[type]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {wageType === 'HOURLY' && (
                <div className="space-y-1.5">
                  <Label htmlFor="ec-hourlyWage">Stundenlohn (EUR)</Label>
                  <Input
                    type="number"
                    id="ec-hourlyWage"
                    min="0"
                    step="0.01"
                    value={hourlyWage}
                    onChange={(e) => setHourlyWage(e.target.value)}
                    placeholder="z.B. 13,90"
                  />
                </div>
              )}

              {wageType === 'FIXED' && (
                <div className="space-y-1.5">
                  <Label htmlFor="ec-monthlySalary">Monatsgehalt (EUR)</Label>
                  <Input
                    type="number"
                    id="ec-monthlySalary"
                    min="0"
                    step="0.01"
                    value={monthlySalary}
                    onChange={(e) => setMonthlySalary(e.target.value)}
                    placeholder="z.B. 2500"
                  />
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="ec-contractualHours">Vertragliche Wochenstunden</Label>
                <Input
                  type="number"
                  id="ec-contractualHours"
                  min="0"
                  max="50"
                  step="0.5"
                  value={contractualHours}
                  onChange={(e) => setContractualHours(e.target.value)}
                  placeholder="z.B. 20"
                />
              </div>
            </div>
          </div>

          {/* 4. Section: Soziales */}
          <Separator />
          <div className="space-y-3">
            <h3 className="text-sm font-medium">Soziales</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="ec-rv"
                  checked={rv}
                  onCheckedChange={(checked) => setRv(checked === true)}
                />
                <Label htmlFor="ec-rv" className="cursor-pointer">
                  RV-pflichtig
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="ec-hansefit"
                  checked={hansefit}
                  onCheckedChange={(checked) => setHansefit(checked === true)}
                />
                <Label htmlFor="ec-hansefit" className="cursor-pointer">
                  Hansefit
                </Label>
              </div>
            </div>
          </div>

          {/* 5. Section: Betriebseinstellungen */}
          <Separator />
          <div className="space-y-3">
            <h3 className="text-sm font-medium">Betriebseinstellungen</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="ec-federalState">Bundesland</Label>
                <Select value={federalState} onValueChange={setFederalState}>
                  <SelectTrigger id="ec-federalState">
                    <SelectValue placeholder="Bundesland wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(FEDERAL_STATE_LABELS).map(([code, name]) => (
                      <SelectItem key={code} value={code}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ec-vacationDays">Jahresurlaub Vollzeit</Label>
                <Input
                  type="number"
                  id="ec-vacationDays"
                  min="20"
                  max="40"
                  value={vacationDaysFullTime}
                  onChange={(e) => setVacationDaysFullTime(e.target.value)}
                  placeholder="z.B. 30"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ec-workDays">Arbeitstage/Woche</Label>
                <Select value={workDaysPerWeekFullTime} onValueChange={setWorkDaysPerWeekFullTime}>
                  <SelectTrigger id="ec-workDays">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5-Tage-Woche</SelectItem>
                    <SelectItem value="6">6-Tage-Woche</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ec-hoursPerDay">Sollstunden/Tag</Label>
                <Input
                  type="number"
                  id="ec-hoursPerDay"
                  min="4"
                  max="12"
                  step="0.5"
                  value={hoursPerDayFullTime}
                  onChange={(e) => setHoursPerDayFullTime(e.target.value)}
                  placeholder="z.B. 8"
                />
              </div>
            </div>
          </div>

          {/* 6. Comment */}
          <Separator />
          <div className="space-y-1.5">
            <Label htmlFor="ec-changeNote">Anmerkung</Label>
            <Textarea
              id="ec-changeNote"
              value={changeNote}
              onChange={(e) => setChangeNote(e.target.value.slice(0, 500))}
              maxLength={500}
              placeholder="Grund der Änderung (optional)"
              rows={2}
            />
            {changeNote.length > 0 && (
              <p className="text-xs text-muted-foreground text-right">
                {changeNote.length}/500
              </p>
            )}
          </div>
        </div>

        {/* Error */}
        {saveError && (
          <div className="mx-4 mb-0 p-2.5 text-sm border border-red-300/60 bg-red-50 dark:border-red-500/20 dark:bg-red-900/10 rounded-lg text-red-700 dark:text-red-300 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            {saveError}
          </div>
        )}

        {/* Footer */}
        <DialogFooter className="flex-row justify-end gap-3 p-4 border-t border-gray-200 dark:border-white/10 sm:space-x-0">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSaving}
          >
            Abbrechen
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={isSaving || !validFrom}
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Speichern...
              </>
            ) : (
              'Speichern'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
